import React, { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import Navbar2 from "./Navbar2";
import NavbarMob from "./NavbarMob";
import Navbar from "./Navbar";
import Footer from "./Footer";
import vector from "../images/home/Vector.png";
import { useNavigate } from "react-router-dom";
import Navbar2Mob from "./Navbar2Mob";

function PostedJobs() {
    const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
    const [jobPosts, setJobPosts] = useState([]);
    const [filteredJobs, setFilteredJobs] = useState([]);
    const [loading, setLoading] = useState(true); // Loading state
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const employeeId = sessionStorage.getItem("employeeId");
    const customerType = sessionStorage.getItem("customerType");
    const navigate = useNavigate();

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [jobToDelete, setJobToDelete] = useState(null);
    const [deletePassword, setDeletePassword] = useState("");
    const [passwordError, setPasswordError] = useState("");

    const DELETE_ADMIN_PASSWORD = process.env.REACT_APP_DELETE_ADMIN_PASSWORD;

    // Filter states
    const [searchTitle, setSearchTitle] = useState("");
    const [searchLocation, setSearchLocation] = useState("");
    const [searchAddress, setSearchAddress] = useState("");

    const editJobPost = (job) => {
        navigate("/editjobpost", {
            state: {
                jobId: job.job_id,
                employeeId: job.employee_id,
                manualJobID: job.manualJobID || "0",
                jobData: job,
            },
        });
    };

    const openDeleteModal = (jobId) => {
        setJobToDelete(jobId);
        setDeletePassword("");
        setPasswordError("");
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setJobToDelete(null);
        setDeletePassword("");
        setPasswordError("");
    };

    const confirmDelete = async () => {
        if (deletePassword !== DELETE_ADMIN_PASSWORD) {
            setPasswordError("Incorrect password. Deletion denied.");
            return;
        }

        if (!jobToDelete) return;

        try {
            const response = await fetch(`${apiBaseUrl}/jobpost/${jobToDelete}`, {
                method: "DELETE",
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Network response was not ok");
            }

            const data = await response.json();

            if (data.success) {
                setJobPosts((prev) => prev.filter((job) => job.job_id !== jobToDelete));
                setFilteredJobs((prev) => prev.filter((job) => job.job_id !== jobToDelete));
                closeDeleteModal();
                alert("Job post deleted successfully");
            }
        } catch (error) {
            console.error("Error deleting job post:", error);
            setPasswordError("Failed to delete: " + error.message);
        }
    };

    // Fetch job posts
    useEffect(() => {
        // If admin, fetch ALL jobs. If employee/other, fetch only their jobs.
        const isAdmin = customerType === "mainAdmin" || customerType === "admin";
        const queryParams = !isAdmin && employeeId ? `?employeeId=${employeeId}` : "";

        fetch(`${apiBaseUrl}/getjobposts${queryParams}`)
            .then((response) => {
                if (!response.ok) throw new Error("Network response was not ok");
                return response.json();
            })
            .then((data) => {
                // Show both posted and pending jobs; do not filter by enable status.
                setJobPosts(data);
                setFilteredJobs(data);
            })
            .catch((error) => {
                console.error("Error fetching job posts:", error);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [apiBaseUrl, employeeId, customerType]);

    // Apply filters whenever search inputs change
    useEffect(() => {
        let filtered = jobPosts;

        if (searchTitle.trim()) {
            filtered = filtered.filter((job) => job.job_title?.toLowerCase().includes(searchTitle.toLowerCase()));
        }

        if (searchLocation.trim()) {
            const loc = searchLocation.toLowerCase();
            filtered = filtered.filter((job) =>
                (job.location?.toLowerCase().includes(loc) || job.address?.toLowerCase().includes(loc))
            );
        }

        if (searchAddress.trim()) {
            filtered = filtered.filter((job) => job.address?.toLowerCase().includes(searchAddress.toLowerCase()));
        }

        setFilteredJobs(filtered);
    }, [searchTitle, searchLocation, searchAddress, jobPosts]);

    // Clear all filters
    const clearFilters = () => {
        setSearchTitle("");
        setSearchLocation("");
        setSearchAddress("");
    };

    function formatJobTitle(title) {
        const lowercaseWords = ["at", "in", "of", "for", "to", "and", "on", "by", "with"];
        return title
            .split(" ")
            .map((word, index) =>
                lowercaseWords.includes(word.toLowerCase()) && index !== 0
                    ? word.toLowerCase()
                    : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
            )
            .join(" ");
    }

    return (
        <div className="flex flex-col min-h-screen">
            {isMobile ? <NavbarMob /> : <Navbar />}
            <div className="md:flex hidden">
                <Navbar2 />
            </div>
            <div className="md:hidden flex flex-col">
                <Navbar2Mob />
            </div>

            <div className="flex flex-col w-full lg:px-12 px-3 h-auto gap-12 mt-12 justify-center items-center pb-12 bg-[#FFFFFF]">
                <span className="text-2xl font-[600] font-display">View Posted Jobs</span>

                {/* Filter Section - Clean & Minimal */}
                <div className="w-full max-w-8xl flex flex-col gap-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <input
                            type="text"
                            placeholder="Search by Job Title..."
                            value={searchTitle}
                            onChange={(e) => setSearchTitle(e.target.value)}
                            className="px-4 py-3 border-2 border-[#C5C5C5] rounded-[10px] focus:outline-none focus:border-[#E22E37] text-base"
                        />
                        <input
                            type="text"
                            placeholder="Filter by Location..."
                            value={searchLocation}
                            onChange={(e) => setSearchLocation(e.target.value)}
                            className="px-4 py-3 border-2 border-[#C5C5C5] rounded-[10px] focus:outline-none focus:border-[#E22E37] text-base"
                        />
                        <input
                            type="text"
                            placeholder="Filter by Address..."
                            value={searchAddress}
                            onChange={(e) => setSearchAddress(e.target.value)}
                            className="px-4 py-3 border-2 border-[#C5C5C5] rounded-[10px] focus:outline-none focus:border-[#E22E37] text-base"
                        />
                    </div>
                    <div className="flex justify-between items-center">
                        <p className="text-sm text-gray-600">
                            Showing <strong>{filteredJobs.length}</strong> of <strong>{jobPosts.length}</strong> job(s)
                        </p>
                        {(searchTitle || searchLocation) && (
                            <button
                                onClick={clearFilters}
                                className="text-[#E22E37] underline hover:text-black text-sm font-medium"
                            >
                                Clear filters
                            </button>
                        )}
                    </div>
                </div>

                {/* Job Cards - EXACTLY SAME AS BEFORE (NO CHANGES TO UI) */}
                <div className="grid md:grid-cols-3 grid-cols-1 w-full gap-3">
                    {loading ? (
                        <div className="col-span-full flex justify-center items-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E22E37]"></div>
                        </div>
                    ) : filteredJobs.length === 0 ? (
                        <div className="col-span-full text-center py-12">
                            <p className="text-xl text-gray-600">No jobs found matching your search.</p>
                        </div>
                    ) : (
                        filteredJobs.map((job) => (
                            <div
                                className="lg:h-[460px] h-[500px] pt-4 border-2 border-[#C5C5C5] w-full rounded-[10px] flex flex-col overflow-hidden"
                                key={job.job_id}
                            >
                                <div className="w-full h-[20%] p-2 gap-2 flex justify-center border-b-2 border-[#C5C5C5] items-center flex-col">
                                    <span className="text-xl font-[700] font-display">{formatJobTitle(job.job_title)}</span>
                                    <div className="flex flex-row gap-2 items-center justify-center pb-4">
                                        <img src={vector} alt="loc" />
                                        <span className="text-base font-[500] font-display">{job.location}</span>
                                        <div
                                            className={`px-3 py-1 rounded-full text-white font-semibold ${job.enable === "on" ? "bg-green-500" : "bg-red-500"
                                                }`}
                                        >
                                            {job.enable === "on" ? "Posted" : "Pending"}
                                        </div>
                                    </div>
                                </div>

                                <div className="w-full h-[85%] flex flex-col bg-white p-4">
                                    {/* Key-Value Rows */}
                                    <div className="flex-1 grid grid-cols-3 gap-x-1 gap-y-1 text-base">
                                        {/* Repeat the rows as shown above */}
                                        <div className="font-display font-[600] col-span-1 text-left">JOB ID</div>
                                        {/* <div className="font-[600] text-center">:</div> */}
                                        <div className="font-[500] text-left break-words col-span-2">
                                            : {job.manualJobID && job.manualJobID !== "0" ? job.manualJobID : job.job_id}
                                        </div>

                                        <div className="font-display font-[600] col-span-1 text-left">JOB</div>
                                        <div className="font-[500] text-left break-words col-span-2">: {job.job}</div>

                                        {/* <div className="font-display font-[600] col-span-1 text-left">JOB TYPE</div>
                                        <div className="font-[500] text-left col-span-2">: {job.job_type}</div> */}

                                        <div className="font-display font-[600] col-span-1 text-left">ADDRESS</div>
                                        <div className="font-[500] text-left col-span-2">: {job.address}</div>

                                        <div className="font-display font-[600] col-span-1 text-left">AREA</div>
                                        <div className="font-[500] text-left col-span-2">: {job.area}</div>

                                        <div className="font-display font-[600] col-span-1 text-left">BOYS / GIRLS</div>
                                        <div className="font-[500] text-left col-span-2">
                                            :{" "}
                                            {job.gender_type === "MALE"
                                                ? "BOYS"
                                                : job.gender_type === "FEMALE"
                                                    ? "GIRLS"
                                                    : job.gender_type === "MALE/FEMALE"
                                                        ? "BOYS/GIRLS"
                                                        : job.gender_type}
                                        </div>

                                        <div className="font-display font-[600] col-span-1 text-left">Salary</div>
                                        <div className="font-[500] text-left col-span-2">
                                            :{" "}
                                            {job.min_salary > 0 && job.max_salary > 0
                                                ? `${job.min_salary} - ${job.max_salary}`
                                                : job.min_salary > 0
                                                    ? job.min_salary
                                                    : job.max_salary > 0
                                                        ? job.max_salary
                                                        : job.salaryType || "Negotiable"}
                                        </div>

                                        <div className="font-display font-[600] col-span-1 text-left">Qualification</div>
                                        <div className="font-[500] text-left col-span-2">
                                            : {job.qualification ? job.qualification : ""}
                                        </div>

                                        <div className="font-display font-[600] col-span-1 text-left">Posted</div>
                                        <div className="font-[500] text-left col-span-2">
                                            : {new Date(job.created_at).toLocaleDateString("en-GB")}
                                        </div>
                                    </div>

                                    {/* Buttons at bottom */}
                                    <div className="flex justify-center gap-4 mt-2 ">
                                        <div
                                            className="flex items-center justify-center w-[80%] h-[38px] bg-[green] rounded-[10px] text-base font-[600] font-display text-[white] cursor-pointer hover:bg-[#174b17]"
                                            onClick={() => editJobPost(job)}
                                        >
                                            Edit
                                        </div>
                                        {customerType === "mainAdmin" && (
                                            <div
                                                className="flex items-center justify-center w-[80%] h-[38px] bg-[red] rounded-[10px] text-base font-[600] font-display text-[white] cursor-pointer hover:bg-[#fe4d4d]"
                                                onClick={() => openDeleteModal(job.job_id)}
                                            >
                                                Delete
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Delete Modal - unchanged */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-white rounded-[20px] p-8 max-w-sm w-full mx-4 shadow-2xl">
                        <h2 className="text-2xl font-bold text-center mb-4 text-red-600">Confirm Delete</h2>
                        <p className="text-center text-gray-700 mb-6">
                            Are you sure you want to permanently delete this job post?
                            <br />
                            <strong>This action cannot be undone.</strong>
                        </p>

                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Admin Password Required</label>
                            <input
                                type="password"
                                value={deletePassword}
                                onChange={(e) => {
                                    setDeletePassword(e.target.value);
                                    setPasswordError("");
                                }}
                                placeholder="Enter admin password"
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-[12px] focus:outline-none focus:border-red-500"
                                autoFocus
                            />
                            {passwordError && <p className="text-red-500 text-sm mt-2 text-center">{passwordError}</p>}
                        </div>

                        <div className="flex justify-center gap-4">
                            <button
                                onClick={closeDeleteModal}
                                className="px-6 py-3 bg-gray-300 text-black rounded-[12px] font-semibold hover:bg-gray-400 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-6 py-3 bg-red-600 text-white rounded-[12px] font-semibold hover:bg-red-700 transition disabled:opacity-50"
                                disabled={!deletePassword}
                            >
                                Delete Permanently
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
}

export default PostedJobs;
